<template>
  <div>
    <b>{{ currentStudentName }}</b>
    さんを進級させず、復習する単元・大問を指示しますか？（進級させる場合は別のタブに移動してください。）
    <table class="table-gray">
      <tbody>
        <tr v-for="notPromotedBook in notPromotedBooks" :key="notPromotedBook.id">
          <td width="20%">
            <v-select
              tag="div"
              placeholder="級・グレード"
              class="select-control background-color-enable"
              :clearable="false"
              :options="[currentTeachingBookTab]"
              label="name"
              :value="currentTeachingBookTab"
            />
          </td>
          <td width="20%">
            <v-select
              tag="div"
              v-model="notPromotedBook.bookPartId"
              placeholder="分野"
              class="select-control background-color-enable"
              :clearable="false"
              :options="currentTeachingBookTab.bookParts"
              :reduce="(option) => option.id"
              label="name"
              @input="updateBookPart(notPromotedBook)"
            />
          </td>
          <td width="20%">
            <v-select
              tag="div"
              v-model="notPromotedBook.bookChapterId"
              placeholder="単元"
              class="select-control"
              :class="backgroundColorBuilder(!notPromotedBook.bookPartId)"
              :clearable="false"
              :disabled="!notPromotedBook.bookPartId"
              :options="bookChapterByPartId(notPromotedBook.bookPartId)"
              :reduce="(option) => option.id"
              label="name"
              @input="updateBookChapter(notPromotedBook)"
            />
          </td>
          <td width="20%">
            <v-select
              tag="div"
              v-model="notPromotedBook.bookBigQuestionId"
              placeholder="大問"
              class="select-control"
              :class="backgroundColorBuilder(!notPromotedBook.bookChapterId)"
              :clearable="false"
              :disabled="!notPromotedBook.bookChapterId"
              :options="bookBigQuestionByChapterId(notPromotedBook.bookPartId, notPromotedBook.bookChapterId)"
              :reduce="(option) => option.id"
              label="name"
              @input="updateBookBigQuestion(notPromotedBook)"
            />
          </td>
          <td>
            <button class="btn-image" @click="removeBook(notPromotedBook.id)" v-if="canRemoveBook">
              <img src="@/assets/images/Button remove.png" alt="" />
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <button class="btn-image" @click="addBook">
      <img src="@/assets/images/Button add.png" alt="" />
    </button>
    <br />
    復習単元・大問を入力せず、メッセージのみを送ることもできます。
    <br />
    メッセージを追加、編集する場合は下欄に入力してください。
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { basicFieldPresent } from '@/presenters/fields';
import find from 'lodash/find';
import last from 'lodash/last';
import isEmpty from 'lodash/isEmpty';

export default {
  props: {
    currentTeachingBookTab: {
      type: Object,
      required: false,
    },
    currentStudent: {
      type: Object,
      required: true,
    },
  },
  computed: {
    currentStudentName() {
      return basicFieldPresent(this.currentStudent, 'name');
    },
    canRemoveBook() {
      return this.notPromotedBooks.length > 1;
    },
    ...mapState({
      notPromotedBooks: (state) => state.student_history.notPromotedBooks,
    }),
  },
  methods: {
    backgroundColorBuilder(disabled) {
      return disabled ? 'background-color-disable' : 'background-color-enable';
    },
    updateBookPart(notPromotedBook) {
      notPromotedBook.bookId = this.currentTeachingBookTab.id;
      notPromotedBook.bookName = this.currentTeachingBookTab.name;
      notPromotedBook.bookPartName = this.bookPartNameBuilder(notPromotedBook);
      notPromotedBook.bookChapterId = null;
      notPromotedBook.bookChapterName = null;
      notPromotedBook.bookBigQuestionId = null;
      notPromotedBook.bookBigQuestionName = null;
      this.$logger.debug('Update existing Not Promoted Book: ' + JSON.stringify(notPromotedBook));
      this.updateNotPromotedBook(notPromotedBook);
    },
    updateBookChapter(notPromotedBook) {
      notPromotedBook.bookChapterName = this.bookChapterNameBuilder(notPromotedBook);
      notPromotedBook.bookBigQuestionId = null;
      notPromotedBook.bookBigQuestionName = null;
      this.$logger.debug('Update existing Not Promoted Book: ' + JSON.stringify(notPromotedBook));
      this.updateNotPromotedBook(notPromotedBook);
    },
    updateBookBigQuestion(notPromotedBook) {
      notPromotedBook.bookBigQuestionName = this.bookBigQuestionNameBuilder(notPromotedBook);
      this.$logger.debug('Update existing Not Promoted Book: ' + JSON.stringify(notPromotedBook));
      this.updateNotPromotedBook(notPromotedBook);
    },
    bookChapterByPartId(partId) {
      if (partId) {
        const bookPart = find(this.currentTeachingBookTab.bookParts, (part) => part.id == partId);
        return bookPart.bookChapters;
      } else {
        return [];
      }
    },
    bookBigQuestionByChapterId(partId, chapterId) {
      const bookChapters = this.bookChapterByPartId(partId);
      if (isEmpty(bookChapters)) return [];

      if (chapterId) {
        const chapter = find(bookChapters, (chapter) => chapter.id == chapterId);
        return chapter.bigQuestions;
      } else {
        return [];
      }
    },
    bookPartNameBuilder(notPromotedBook) {
      const partId = notPromotedBook.bookPartId;
      if (partId != null) {
        const bookPart = find(this.currentTeachingBookTab.bookParts, (part) => part.id == partId);
        return bookPart.name;
      } else {
        return null;
      }
    },
    bookChapterNameBuilder(notPromotedBook) {
      const partId = notPromotedBook.bookPartId;
      const bookChapters = this.bookChapterByPartId(partId);
      if (isEmpty(bookChapters)) return null;

      const chapterId = notPromotedBook.bookChapterId;
      if (chapterId) {
        const chapter = find(bookChapters, (chapter) => chapter.id == chapterId);
        return chapter.name;
      } else {
        return null;
      }
    },
    bookBigQuestionNameBuilder(notPromotedBook) {
      const partId = notPromotedBook.bookPartId;
      const chapterId = notPromotedBook.bookChapterId;
      const bookBigQuestions = this.bookBigQuestionByChapterId(partId, chapterId);
      if (isEmpty(bookBigQuestions)) return null;

      const bigQuestionId = notPromotedBook.bookBigQuestionId;
      if (bigQuestionId) {
        const bigQuestion = find(bookBigQuestions, (bigQuestion) => bigQuestion.id == bigQuestionId);
        return bigQuestion.name;
      } else {
        return null;
      }
    },
    newNotPromotedBook() {
      const lastNotPromotedBook = last(this.notPromotedBooks);
      const newNotPromotedBookId = lastNotPromotedBook.id + 1;
      this.$logger.debug('Preparing new NotPromotedBook with ID ' + newNotPromotedBookId);
      return {
        id: newNotPromotedBookId,
        bookId: this.currentTeachingBookTab.id,
        bookName: this.currentTeachingBookTab.name,
        bookPartId: null,
        bookPartName: null,
        bookChapterId: null,
        bookChapterName: null,
        bookBigQuestionId: null,
        bookBigQuestionName: null,
      };
    },
    addBook() {
      let hasNotCompletedBook = find(this.notPromotedBooks, (book) => book.bookBigQuestionId == null);
      if (!hasNotCompletedBook) {
        const book = this.newNotPromotedBook();
        this.$logger.debug('Added new NotPromotedBook: ' + JSON.stringify(book));
        this.addNotPromotedBook(book);
      }
    },
    removeBook(bookId) {
      this.$logger.debug('Removed existing NotPromotedBook with Id: ' + bookId);
      this.deleteNotPromotedBook(bookId);
    },
    ...mapActions('student_history', ['updateNotPromotedBook', 'addNotPromotedBook', 'deleteNotPromotedBook']),
  },
};
</script>

<style scoped>
.table-gray {
  width: 100%;
}
.table-gray td {
  padding-right: 10px;
}
.select-control {
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 12px;
}
.btn-image {
  margin-left: 0px;
  margin-top: 10px;
  margin-bottom: 10px;
}
.background-color-enable {
  background-color: white;
}
.background-color-disable {
  background-color: transparent;
}
</style>
